import {useEffect,useMemo,useState} from "react";
import {Link} from "react-router-dom";
import {useAuth} from "@/contexts/AuthContext";
import {apiFetch} from "@/lib/api-fetch";
import {previewMix} from "../../../shared/yard-staging";
import {Package,ArrowRight} from "lucide-react";

type Cooking = {id:number;row_id:string;name:string;location:string|null};
type Recipe = {id?:number;version?:number;name:string;basis:"volume"|"mass";lines:{ingredientId:number;percent:number;name?:string}[]};
type Staging = {
  id:number;row_id:string;name:string;
  remainingVolume:number|null;remainingTons:number|null;
  inboundVolume:number|null;inboundTons:number|null;
  additions:{id:number;material_name:string;cubic_yards:number|null;weight:number|null;notes:string|null;added_at:string}[];
  transfers:{id:number;destination_windrow_id:number;volume:number|null;tons:number|null;created_at:string;notes:string|null;lines?:{material_name:string;cubic_yards:number|null;weight:number|null}[]}[];
  cooking:Cooking[];
};

const qty=(n:number|null|undefined,unit:string)=>n==null?"—":`${Number(n).toLocaleString(undefined,{maximumFractionDigits:1})} ${unit}`;

export default function StagingMixPanel({windrowId,onMixed}:{windrowId:number;onMixed:()=>void}){
  const {token}=useAuth();
  const [data,setData]=useState<Staging|null>(null);
  const [recipes,setRecipes]=useState<Recipe[]>([]);
  const [error,setError]=useState("");
  const [busy,setBusy]=useState(false);
  const [mode,setMode]=useState<"existing"|"new">("existing");
  const [recipeId,setRecipeId]=useState("");
  const [volume,setVolume]=useState("12");
  const [tons,setTons]=useState("");
  const [destId,setDestId]=useState("");
  const [destName,setDestName]=useState("");
  const [destRowId,setDestRowId]=useState("");
  const [destLocation,setDestLocation]=useState("Pad A");
  const [notes,setNotes]=useState("");

  const load=()=>{
    Promise.all([
      apiFetch(`/api/staging/${windrowId}`,{headers:{Authorization:`Bearer ${token}`}}).then(async r=>{const j=await r.json();if(!r.ok)throw new Error(j.error);return j;}),
      apiFetch("/api/local-demo/recipes",{headers:{Authorization:`Bearer ${token}`}}).then(r=>r.ok?r.json():{recipes:[]}).catch(()=>({recipes:[]})),
      apiFetch("/api/field/catalog",{headers:{Authorization:`Bearer ${token}`}}).then(r=>r.ok?r.json():{recipes:[]}).catch(()=>({recipes:[]})),
    ]).then(([staging,local,field])=>{
      setData(staging);
      const list=(local.recipes?.length?local.recipes:field.recipes)||[];
      setRecipes(list);
      if(!recipeId && list[0]) setRecipeId(String(list[0].id??0));
      if(!destId && staging.cooking[0]) setDestId(String(staging.cooking[0].id));
      setError("");
    }).catch(e=>setError(e.message||"Could not load staging."));
  };
  useEffect(()=>{load();},[windrowId,token]);

  const recipe=recipes.find(r=>String(r.id??0)===recipeId)||recipes[0];
  const preview=useMemo(()=>{
    if(!recipe?.lines?.length) return null;
    try{return previewMix({name:recipe.name,basis:recipe.basis,lines:recipe.lines.map(l=>({...l,densityLbsPerCuft:null}))},{volume:volume?Number(volume):null,tons:tons?Number(tons):null});}
    catch{return null;}
  },[recipe,volume,tons]);

  const mix=async()=>{
    if(!data||!recipe) return;
    setBusy(true);setError("");
    try{
      const actionId=`mix_${Date.now().toString(36)}_${Math.random().toString(36).slice(2,10)}`;
      const body={
        actionId,
        recipe:{name:recipe.name,basis:recipe.basis,lines:recipe.lines.map(l=>({ingredientId:l.ingredientId,percent:l.percent})),templateId:recipe.id??null,templateVersion:recipe.version??null},
        mix:{volume:volume?Number(volume):null,tons:tons?Number(tons):null},
        destination:mode==="existing"?{mode:"existing",windrowId:Number(destId)}:{mode:"new",name:destName.trim(),rowId:destRowId.trim(),location:destLocation.trim(),shape:"line"},
        notes,
      };
      const res=await apiFetch(`/api/staging/${windrowId}/mix`,{method:"POST",headers:{Authorization:`Bearer ${token}`,"Content-Type":"application/json"},body:JSON.stringify(body)});
      const json=await res.json();
      if(!res.ok) throw new Error(json.error||"Mix failed.");
      setNotes("");
      load();
      onMixed();
    }catch(e){setError((e as Error).message);}
    finally{setBusy(false);}
  };

  if(!data) return error?<p role="alert" className="text-sm text-amber-800">{error}</p>:null;

  return (
    <section aria-label="Staging pile" className="space-y-4 rounded-xl border border-earth-500/40 bg-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="label-caps text-earth-500">Staging / receiving</p>
          <h2 className="heading-display text-xl text-foreground mt-1">Hold inbound, then mix to a pad</h2>
          <p className="text-sm text-muted-foreground mt-1">Logged loads sit here until you mix them to a recipe and move them onto a cooking windrow. Mix does not deduct product inventory.</p>
        </div>
        <div className="text-right text-sm">
          <p className="font-semibold text-foreground">{qty(data.remainingVolume,"yd³")} remaining</p>
          <p className="text-xs text-muted-foreground">{qty(data.inboundVolume,"yd³")} inbound · {data.transfers.length} mix{data.transfers.length===1?"":"es"}</p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <h3 className="text-sm font-semibold">Inbound material</h3>
          {data.additions.length?<ul className="text-sm space-y-2">{data.additions.slice().reverse().map(row=>(
            <li key={row.id} className="rounded-lg border border-border px-3 py-2">
              <div className="flex justify-between gap-2"><span className="font-medium">{row.material_name}</span><span className="tabular-nums">{qty(row.cubic_yards,"yd³")}</span></div>
              <p className="text-xs text-muted-foreground">{new Date(row.added_at).toLocaleString()}{row.notes?` · ${row.notes}`:""}</p>
            </li>
          ))}</ul>:<p className="text-sm text-muted-foreground">No inbound loads yet. <Link className="text-forest-600 font-semibold" to={`/app/log-material?windrow=${windrowId}`}>Log material</Link> into this pile.</p>}
          <Link to={`/app/log-material?windrow=${windrowId}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-forest-600"><Package size={14}/>Log inbound material</Link>
        </div>
        <form className="space-y-3 rounded-lg border border-border p-3 bg-background" onSubmit={e=>{e.preventDefault();mix();}}>
          <h3 className="text-sm font-semibold">Mix to a cooking pad</h3>
          <label className="block text-xs font-semibold">Recipe
            <select aria-label="Mix recipe" value={recipeId} onChange={e=>setRecipeId(e.target.value)} className="mt-1 w-full min-h-11 rounded-lg border border-border bg-background px-3 text-sm">
              {recipes.map(r=><option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs font-semibold">Volume (yd³)<input aria-label="Mix volume" value={volume} onChange={e=>setVolume(e.target.value)} className="mt-1 w-full min-h-11 rounded-lg border border-border bg-background px-3 text-sm"/></label>
            <label className="text-xs font-semibold">Weight (US tons)<input aria-label="Mix weight" value={tons} onChange={e=>setTons(e.target.value)} className="mt-1 w-full min-h-11 rounded-lg border border-border bg-background px-3 text-sm"/></label>
          </div>
          <div className="flex gap-2">{(["existing","new"] as const).map(value=>(
            <button type="button" key={value} aria-pressed={mode===value} onClick={()=>setMode(value)} className={`px-3 py-2 rounded-lg border text-xs font-semibold ${mode===value?"bg-forest-600 text-white border-forest-600":"border-border text-muted-foreground"}`}>{value==="existing"?"Existing pad":"New cooking row"}</button>
          ))}</div>
          {mode==="existing"?<label className="block text-xs font-semibold">Cooking windrow
            <select aria-label="Cooking destination" value={destId} onChange={e=>setDestId(e.target.value)} className="mt-1 w-full min-h-11 rounded-lg border border-border bg-background px-3 text-sm">
              {data.cooking.map(row=><option key={row.id} value={row.id}>{row.name} · {row.row_id}{row.location?` · ${row.location}`:""}</option>)}
            </select>
          </label>:<div className="grid gap-2">
            <label className="text-xs font-semibold">New row ID<input aria-label="New cooking row ID" value={destRowId} onChange={e=>setDestRowId(e.target.value)} className="mt-1 w-full min-h-11 rounded-lg border border-border bg-background px-3 text-sm"/></label>
            <label className="text-xs font-semibold">Name<input aria-label="New cooking name" value={destName} onChange={e=>setDestName(e.target.value)} className="mt-1 w-full min-h-11 rounded-lg border border-border bg-background px-3 text-sm"/></label>
            <label className="text-xs font-semibold">Pad / location<input aria-label="New cooking location" value={destLocation} onChange={e=>setDestLocation(e.target.value)} className="mt-1 w-full min-h-11 rounded-lg border border-border bg-background px-3 text-sm"/></label>
          </div>}
          <label className="block text-xs font-semibold">Notes<input value={notes} onChange={e=>setNotes(e.target.value)} className="mt-1 w-full min-h-11 rounded-lg border border-border bg-background px-3 text-sm"/></label>
          {preview?.rows?.length?<ul className="text-xs text-muted-foreground space-y-1">{preview.rows.map(row=><li key={row.ingredientId} className="flex justify-between gap-2"><span>{row.name} · {row.percent}%</span><span>{qty(row.volume,"yd³")}</span></li>)}</ul>:null}
          {error&&<p role="alert" className="text-sm text-red-700">{error}</p>}
          <button type="submit" disabled={busy||!recipe} className="inline-flex items-center gap-2 min-h-11 px-4 rounded-xl bg-forest-600 text-white text-sm font-semibold disabled:opacity-50">{busy?"Mixing…":"Mix onto cooking pad"}<ArrowRight size={14}/></button>
        </form>
      </div>

      {data.transfers.length>0&&<div>
        <h3 className="text-sm font-semibold mb-2">Mix history</h3>
        <ul className="text-sm space-y-2">{data.transfers.map(row=>{
          const dest=data.cooking.find(c=>c.id===row.destination_windrow_id);
          return <li key={row.id} className="rounded-lg border border-border px-3 py-2 flex justify-between gap-3">
            <span>Pulled {qty(row.volume,"yd³")} {dest?`to ${dest.name}`:`to row ${row.destination_windrow_id}`}</span>
            <span className="text-xs text-muted-foreground">{new Date(row.created_at).toLocaleString()}</span>
          </li>;
        })}</ul>
      </div>}
    </section>
  );
}
